import { useEffect, useState } from 'react';
import { ArchiveRestore, ArrowLeft, Search } from 'lucide-react';

const PAGE_SIZE = 30;

const formatDate = value => (value ? new Date(value).toLocaleDateString('pl-PL') : '');

// Read-only list of a single project's archived tasks. Tasks keep their status here;
// opening one shows it in the regular task drawer and restoring returns it to the board.
export default function TaskArchive({
  organizationId, service, statuses, priorities, reloadKey, busy, onOpen, onRestore, onBack,
  renderPeople, errorMessage, onAccessLost,
}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState('');

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(timeout);
  }, [search]);

  const params = offset => ({
    archived: true,
    status: status || undefined,
    search: debouncedSearch || undefined,
    limit: PAGE_SIZE,
    offset,
  });

  useEffect(() => {
    let active = true;
    setLoading(true);
    service.tasks(organizationId, params(0)).then(page => {
      if (!active) return;
      setItems(page.items);
      setTotal(page.total);
      setError('');
    }).catch(loadError => {
      if (!active) return;
      setItems([]);
      setTotal(0);
      setError(errorMessage(loadError));
      if ([403, 404].includes(loadError.response?.status)) onAccessLost();
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, debouncedSearch, status, reloadKey]);

  const loadMore = async () => {
    setLoadingMore(true);
    setError('');
    try {
      const page = await service.tasks(organizationId, params(items.length));
      setItems(current => {
        const ids = new Set(current.map(task => task.id));
        return [...current, ...page.items.filter(task => !ids.has(task.id))];
      });
      setTotal(page.total);
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoadingMore(false);
    }
  };

  const filtered = Boolean(debouncedSearch || status);

  return <section className="tp-archive" aria-labelledby="tp-archive-title">
    <div className="tp-archive-header">
      <div>
        <h4 id="tp-archive-title">Archiwum zadań</h4>
        <p>Zadania z archiwum nie są widoczne na tablicy i zachowują swój status. Możesz je przeglądać i przywracać.</p>
      </div>
      <button type="button" onClick={onBack}><ArrowLeft size={17} /> Wróć do tablicy</button>
    </div>
    <div className="tp-filters tp-archive-filters">
      <label className="tp-archive-search"><Search size={16} />
        <input aria-label="Szukaj w archiwum" placeholder="Szukaj po tytule lub opisie…" value={search} onChange={event => setSearch(event.target.value)} />
      </label>
      <select aria-label="Filtruj archiwum po statusie" value={status} onChange={event => setStatus(event.target.value)}>
        <option value="">Wszystkie statusy</option>
        {Object.entries(statuses).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </select>
    </div>
    {error && <p className="tp-error" role="alert">{error}</p>}
    {loading ? <p role="status">Ładowanie archiwum…</p> : <>
      <p className="tp-archive-count">Zadania w archiwum: {total}</p>
      {!!items.length && <ul className="tp-archive-list">
        {items.map(task => <li key={task.id} className="tp-archive-item">
          <button type="button" className="tp-archive-open" onClick={() => onOpen(task)}
            aria-label={`Otwórz zadanie #${task.id} ${task.title}`}>
            <span className="tp-archive-title"><span className="tp-task-id">#{task.id}</span><strong>{task.title}</strong></span>
            <span className="tp-archive-meta">
              <span className={`tp-archive-status tp-archive-status-${task.status}`}>{statuses[task.status] || task.status}</span>
              <span className={`tp-priority tp-priority-${task.priority}`}>{priorities[task.priority]}</span>
              <small>Zarchiwizowano {formatDate(task.archived_at)}{task.archived_by ? ` · ${task.archived_by.full_name}` : ''}</small>
            </span>
          </button>
          <span className="tp-archive-people">{renderPeople(task.assignees || [])}</span>
          <button type="button" className="tp-archive-restore" disabled={busy}
            aria-label={`Przywróć zadanie #${task.id} na tablicę`} onClick={() => onRestore(task)}>
            <ArchiveRestore size={16} /> Przywróć
          </button>
        </li>)}
      </ul>}
      {!items.length && <p className="tp-archive-empty">
        {filtered ? 'Brak zadań w archiwum spełniających kryteria.' : 'Archiwum tego projektu jest puste.'}
      </p>}
      {items.length < total && <button type="button" className="tp-load-more" disabled={loadingMore} onClick={loadMore}>
        {loadingMore ? 'Ładowanie…' : `Załaduj więcej (${total - items.length})`}
      </button>}
    </>}
  </section>;
}
